import { api } from "./client";
import type { BillCardPayment } from "./bills";

export interface BillHistoryCancellation {
  id: number;
  cancelled_by_user_id: number;
  cancelled_at: string;
  reason: string | null;
  is_defective: boolean;
}

export interface BillHistoryModifier {
  id: number;
  modifier_id: string;
  created_at: string;
}

// Всички Decimal полета от BE идват като JSON НИЗ (виж CLAUDE.md) — парсвай
// явно преди аритметика.
export interface BillHistoryAddition {
  id: number;
  addition_item_id: number;
  station_id: number;
  quantity: string;
  price: string;
  percent: string;
  total_sum: string;
  status: string;
  item_names: Record<string, string>;
}

export interface BillHistoryItem {
  id: number;
  item_id: number;
  station_id: number;
  quantity: string;
  price: string;
  percent: string;
  total_sum: string;
  status: string;
  status_name: string;
  is_cancelled: boolean;
  cancellation: BillHistoryCancellation | null;
  item_names: Record<string, string>;
  modifiers: BillHistoryModifier[];
  additions: BillHistoryAddition[];
}

export interface BillHistoryOrder {
  id: number;
  waiter_id: number;
  order_time: string;
  comment: string | null;
  is_split: boolean;
  split_from_bill_id: number | null;
  split_from_bill_name: string | null;
  items: BillHistoryItem[];
}

export interface BillHistoryStatistics {
  total_orders: number;
  total_items: number;
  active_items: number;
  cancelled_items: number;
  split_orders: number;
}

export interface BillHistory {
  id: number;
  name: string;
  status: "open" | "closed";
  created_at: string;
  closed_at: string | null;
  duration_minutes: number | null;
  total_amount: string;
  total: string;
  waiter_id: number | null;
  customer_id: number | null;
  payment_method_id: number | null;
  payment_method_name: string | null;
  mixed_cash_amount: string | null;
  mixed_card_amount: string | null;
  // Номерът на касовата бележка от фискалното устройство. Пълни се САМО при
  // успешна фискализация — отворена или закрита нефискализирана сметка е null.
  receipt_number: string | null;
  statistics: BillHistoryStatistics;
  orders: BillHistoryOrder[];
  // True ако вече има издадена фактура за тази сметка — BE проверява в
  // invoice_bills. Ползва се за да блокираме повторно фактуриране.
  is_invoiced?: boolean;
  // Картовите тегления по тази инстанция на сметката (регистъра
  // `card_payments`; празно = плащане без терминал).
  card_payments?: BillCardPayment[];
  // Отстъпката, както я е ЗАПИСАЛО BE-то (`bills.total_discount`) — НЕ я
  // извеждай като „бруто − сума", защото това брои и редовете със сменена
  // цена като отстъпка. Колоната в базата е NOT NULL DEFAULT 0, но схемата е
  // Optional → парсвай през `?? "0"`.
  total_discount?: string | null;
  discount_percent?: string | null;
  // Възстановена ли е сметката (отворена наново след приключване, СЪЩОТО
  // id) — кога за последно и колко пъти. Плащането и бонът по-горе са на
  // ТЕКУЩОТО приключване; отменените са в „История на операциите".
  restored_at?: string | null;
  restores_count?: number;
  // Броят гости от „Маркиране" (`bills.guests`); null = не е въвеждан.
  guests?: number | null;
}

export type BillStatus = "open" | "closed";

export type BillsHistorySortBy =
  // Номерът на сметката в базата; ключът се казва като колоната в таблицата.
  | "bill_id"
  | "name"
  | "status"
  | "created_at"
  | "closed_at"
  | "duration_minutes"
  | "total_orders"
  | "cancelled_items"
  | "payment_method_name"
  | "receipt_number"
  | "total_amount"
  | "fiscal_amount"
  | "total_discount"
  | "items_quantity"
  | "cancelled_total"
  | "guests";

export type BillsHistorySortOrder = "asc" | "desc";

export interface BillsHistoryFilters {
  bill_id?: number;
  statuses?: BillStatus[];
  waiter_ids?: number[];
  payment_method_ids?: number[];
  start_date?: string;
  end_date?: string;
  // Кое поле да се ползва за date range. По default backend ползва created_at;
  // ползвай "closed_at" в Ordering Restore, за да виждаш бил-и затворени в
  // прозореца, дори да са създадени по-рано.
  date_field?: "created_at" | "closed_at";
  sort_by?: BillsHistorySortBy;
  sort_order?: BillsHistorySortOrder;
  page?: number;
  page_size?: number;
  // true → връща само сметки, които са в някой отчет;
  // false → връща само сметки извън всеки отчет (за /ordering Фактуриране).
  in_report?: boolean;
  // true → само сметки с ИЗДАДЕН фискален бон; false → само тези без номер.
  // Комбинира се със `statuses: ["closed"]`, защото отворената сметка също
  // няма номер, а въпросът е за приключените.
  has_receipt?: boolean;
  // true → връща само сметки, които вече са във фактура;
  // false → връща само сметки извън всяка фактура (за /ordering Фактуриране,
  // за да не може същата сметка да бъде фактурирана повторно).
  in_invoice?: boolean;
  // Контекст на извикването — определя кое право дава "всички сметки":
  // "ordering" (default; Възстановяване/Фактуриране в Маркиране) →
  //   view_all_users_bills; "reports" (справките История на сметки, Сторно,
  //   Отстъпки, Фактури) → правото за съответната справка. Без право → BE
  //   връща само своите сметки, независимо от контекста.
  context?: "ordering" | "reports";
  // Търсене по ИМЕ НА МАСА, без значение малки/главни букви. Сервитьорът НЕ се
  // търси оттук — името му живее в org базата, не в тази; за него подавай
  // `waiter_ids`, решени от вече заредения списък потребители.
  q?: string;
  // Хистограмата на активността: "auto" = по часове при период от един ден,
  // иначе по дни (заварено); "hour" = винаги 24 часови кофи; "day" = по дни.
  activity_granularity?: "auto" | "hour" | "day";
  // true → само `summary` + `activity`, без списъка. Ползва се за предходния
  // период при делтите: `page_size=1` НЕ е леко, защото BE-то зарежда всички
  // сметки на периода с редовете им, преди да страницира.
  summary_only?: boolean;
}

// Запазваме старото име като alias за обратна съвместимост на типа,
// използваните полета са същите.
export type BillHistoryFilters = BillsHistoryFilters;

export interface BillsHistorySummary {
  total_count: number;
  open_count: number;
  closed_count: number;
  // Decimal → JSON низ
  total_revenue: string;
  avg_ticket: string;
  // Средното време на маса за ЦЕЛИЯ период, в минути, само по закритите
  // сметки. null = нула закрити сметки в периода.
  avg_duration_minutes?: string | null;
  // Закрити сметки с фискален бон и без — брой и СУМА (двете суми заедно дават
  // `total_revenue`). ВНИМАНИЕ: при `session_access: false` закритите В БРОЙ
  // без бон („Закриване на сметка(1)") са скрити от целия отговор, тоест
  // `unfiscalized_*` не са пълната картина — тогава НЕ ги показвай.
  // Опционални срещу по-старо BE.
  fiscalized_count?: number;
  fiscalized_amount?: string;
  unfiscalized_count?: number;
  unfiscalized_amount?: string;
}

// Редът „Общо" за ЦЕЛИЯ период по активните филтри — не за страницата.
// `summary` не върши тази работа: то е само по закритите сметки и умишлено
// игнорира `statuses` / `payment_method_ids` / `q`.
export interface BillsHistoryTotals {
  bills: number;
  // Decimal → JSON низ
  items_quantity: string;
  discount_total: string;
  cancelled_total: string;
  amount_total: string;
  // „Сума ФП" за същите сметки (виж `fiscalAmountOf`). Опционално срещу
  // по-старо BE.
  fiscal_amount_total?: string;
  // Сборът на въведените бройки гости. Опционално срещу по-старо BE.
  guests_total?: number;
}

export interface BillsHistoryActivityPoint {
  key: string;
  label: string;
  count: number;
}

export interface BillsHistoryActivity {
  granularity: "hour" | "day";
  points: BillsHistoryActivityPoint[];
}

export interface BillsHistoryResponse {
  items: BillHistory[];
  total: number;
  summary: BillsHistorySummary;
  // Незадължително в типа, за да не се чупи FE срещу по-старо BE.
  totals?: BillsHistoryTotals;
  activity: BillsHistoryActivity;
  page: number;
  page_size: number;
  // false = BE е скрил закритите в брой без бон („Закриване на сметка(1)")
  // от този отговор (archived ресторант + сесия без access) — разбивката по
  // фискализация тогава не е пълна и се крие.
  session_access: boolean;
  // true = архивиран ресторант + код 2: „Сума ФП" е сумата само на сметките с
  // бон. false (или липсва — по-старо BE) = „Сума ФП" е равна на „Сума".
  fiscal_split?: boolean;
}

/**
 * „Сума ФП" на сметка — огледало на `reports/fiscal_amount.py` в BE (там се
 * смятат редът „Общо" и сортирането): без разделяне е цялата сума; при
 * разделяне (код 2) — сумата само ако има издаден бон, иначе 0.
 */
export const fiscalAmountOf = (
  bill: Pick<BillHistory, "total_amount" | "receipt_number">,
  fiscalSplit: boolean,
): number =>
  !fiscalSplit || bill.receipt_number
    ? parseFloat(bill.total_amount) || 0
    : 0;

export const getBillsHistory = async (
  filters?: BillsHistoryFilters,
): Promise<BillsHistoryResponse> => {
  const params = new URLSearchParams();

  if (filters?.bill_id) params.append("bill_id", String(filters.bill_id));
  filters?.statuses?.forEach((s) => params.append("statuses", s));
  filters?.waiter_ids?.forEach((id) =>
    params.append("waiter_ids", String(id)),
  );
  filters?.payment_method_ids?.forEach((id) =>
    params.append("payment_method_ids", String(id)),
  );
  if (filters?.start_date) params.append("start_date", filters.start_date);
  if (filters?.end_date) params.append("end_date", filters.end_date);
  if (filters?.date_field) params.append("date_field", filters.date_field);
  if (filters?.sort_by) params.append("sort_by", filters.sort_by);
  if (filters?.sort_order) params.append("sort_order", filters.sort_order);
  if (filters?.page) params.append("page", String(filters.page));
  if (filters?.page_size)
    params.append("page_size", String(filters.page_size));
  if (filters?.in_report !== undefined)
    params.append("in_report", String(filters.in_report));
  if (filters?.in_invoice !== undefined)
    params.append("in_invoice", String(filters.in_invoice));
  if (filters?.has_receipt !== undefined)
    params.append("has_receipt", String(filters.has_receipt));
  if (filters?.context) params.append("context", filters.context);
  if (filters?.q?.trim()) params.append("q", filters.q.trim());
  if (filters?.activity_granularity)
    params.append("activity_granularity", filters.activity_granularity);
  if (filters?.summary_only)
    params.append("summary_only", String(filters.summary_only));

  const queryString = params.toString();
  const url = queryString ? `/bills/history?${queryString}` : "/bills/history";

  const response = await api.get<BillsHistoryResponse>(url);
  return response.data;
};

// Взима историята на конкретна сметка по нейното ID чрез филтриране по bill_id
export const getBillHistoryById = async (
  billId: number,
  context?: "ordering" | "reports",
): Promise<BillHistory | null> => {
  const result = await getBillsHistory({ bill_id: billId, context });
  return result.items.length > 0 ? result.items[0] : null;
};
